import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RequestService } from './request.service';

@Injectable({
  providedIn: 'root',
})
export abstract class RequestEntitiesService<T> {
  protected readonly requestService = inject(RequestService);
  private load$ = new ReplaySubject<void>(1);
  private readonly cacheValid$ = new BehaviorSubject<boolean>(false);
  protected readonly initialValue: T = [] as T;
  protected readonly destroyRef = inject(DestroyRef);
  abstract readonly source$: (requestId: number) => Observable<T>;
  private data$ = this.load$.pipe(
    debounceTime(100),
    withLatestFrom(this.requestService.requestId$, this.cacheValid$),
    filter(([, , cacheValid]) => !cacheValid),
    map(([, requestId]) => requestId),
    switchMap(requestId => this.source$(requestId)),
    tap(() => this.cacheValid$.next(true)),
    shareReplay(1),
    takeUntilDestroyed(this.destroyRef),
  );

  constructor() {
    this.requestService.requestId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cacheValid$.next(false));
  }

  entitiesUnsubscribe() {
    this.load$.complete();
    this.cacheValid$.complete();
  }

  get entities$() {
    this.load$.next();

    return this.data$;
  }

  get $entities() {
    this.load$.next();

    return toSignal(this.data$, {
      initialValue: this.initialValue,
    });
  }

  refreshEntities() {
    this.cacheValid$.next(false);
    this.load$.next();
  }

  resetCache() {
    this.load$.complete(); // Complete the old subject to clean up subscribers
    this.cacheValid$.next(false);
    this.load$ = new ReplaySubject<void>(1);

    this.data$ = this.load$.pipe(
      debounceTime(100),
      withLatestFrom(this.requestService.requestId$, this.cacheValid$),
      filter(([, , cacheValid]) => !cacheValid),
      map(([, requestId]) => requestId),
      switchMap(requestId => this.source$(requestId)),
      tap(() => this.cacheValid$.next(true)),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
